import { useResponsiveFontSize } from '@/components/Util/reactResponsiveDimensions';
import { useRef, useState } from 'react';
import { FlatList, Modal, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import Icon from '@expo/vector-icons/FontAwesome';
import colors, { appTheme } from '@/config/colors';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { useOverlayGuard } from '@/hooks/useOverlayGuard';

const Dropdown = ({
  label,
  data,
  onSelect,
  dropDownContentWidth,
  fontSize,
  defaultValue,
  customTopValue,
  color,
  fontWeight,
}) => {
  const DropdownButton = useRef();
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState(defaultValue ?? undefined);
  const [dropdownTop, setDropdownTop] = useState(0);
  const responsiveFontSize = useResponsiveFontSize(1);
  useOverlayGuard(visible);
  const responsiveStyles = StyleSheet.create({
    dropDownInputStyle: {
      width: useResponsiveFontSize(20),
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    deviderStyle: {
      marginVertical: useResponsiveFontSize(1.2),
    },
    dropdownPosition: {
      top: customTopValue ? dropdownTop / customTopValue : dropdownTop / 2,
      width: dropDownContentWidth,
      right: 4,
    },
  });

  const getResponsiveFontSize = (factor) => {
    return responsiveFontSize * factor;
  };

  const iconSize = getResponsiveFontSize(1.7);

  const toggleDropdown = () => {
    visible ? setVisible(false) : openDropdown();
  };

  const openDropdown = () => {
    DropdownButton.current.measure((_fx, _fy, _w, h, _px, py) => {
      setDropdownTop(py + h * 3);
    });
    setVisible(true);
  };

  const onItemPress = (item) => {
    setSelected(item);
    onSelect(item);
    setVisible(false);
  };

  const buttonStyle = {
    fontSize: fontSize,
    color: color ? color : appTheme.textColor,
    fontWeight: fontWeight ? fontWeight : 'normal',
  };

  const renderItem = ({ item, index }) => (
    <PressableNeolexon onPress={() => onItemPress(item)}>
      <Text style={buttonStyle}>{item.label}</Text>
      {data.length !== index + 1 && (
        <View style={[styles.bottomLine, responsiveStyles.deviderStyle]}></View>
      )}
    </PressableNeolexon>
  );

  const renderDropdown = () => {
    return (
      <Modal
        visible={visible}
        transparent
        animationType="none"
        supportedOrientations={[
          'landscape',
          'landscape-left',
          'landscape-right',
        ]}
      >
        <PressableNeolexon
          style={styles.overlay}
          onPress={() => setVisible(false)}
        >
          <View style={[styles.dropdown, responsiveStyles.dropdownPosition]}>
            <FlatList
              data={data}
              renderItem={renderItem}
              keyExtractor={(item, index) => index.toString()}
            />
          </View>
        </PressableNeolexon>
      </Modal>
    );
  };

  return (
    <PressableNeolexon
      ref={DropdownButton}
      style={[styles.button, responsiveStyles.dropDownInputStyle]}
      onPress={toggleDropdown}
    >
      {renderDropdown()}
      <Text
        style={
          (styles.buttonText,
          {
            fontSize: fontSize,
            color: color ? color : appTheme.textColor,
            fontWeight: fontWeight ? fontWeight : 'normal',
          })
        }
      >
        {(!!selected && selected.label) || label}
      </Text>
      <Icon name="chevron-down" size={iconSize} />
    </PressableNeolexon>
  );
};

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.white,
    borderRadius: 8,
    padding: 5,
    zIndex: 1,
  },
  buttonText: {
    flex: 1,
    textAlign: 'center',
  },
  dropdown: {
    position: 'absolute',
    backgroundColor: '#fff',
    width: '100%',
    borderWidth: 2,
    borderRadius: 8,
    padding: 10,
  },
  overlay: {
    width: '100%',
    height: '100%',
  },
  bottomLine: {
    borderBottomWidth: 2,
    borderBottomColor: colors.darkGrey,
  },
});

export default Dropdown;
